import type { Lang } from '@aera/schema';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EASE } from '@aera/ui';
import aeraLogo from '../assets/aera-logo.png';

type Variant = 'primary' | 'gradient' | 'ghost' | 'outline' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-black text-white hover:bg-black/85 disabled:bg-black/30',
  // The gradient is an accent: one button (Start), never a background.
  gradient: 'text-white lp-gradient-bar hover:brightness-110 disabled:opacity-40',
  ghost: 'text-ink-2 hover:text-black hover:bg-surface disabled:opacity-40',
  outline: 'border border-hair text-black hover:bg-surface disabled:opacity-40',
  danger: 'border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40',
};

export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg'; busy?: boolean }) {
  const sizes = { sm: 'h-8 px-3 text-[13px]', md: 'h-11 px-5 text-[15px]', lg: 'h-12 px-7 text-[16px]' };
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-medium transition-[background-color,transform,opacity] duration-200 active:scale-[0.98] disabled:cursor-not-allowed ${sizes[size]} ${VARIANTS[variant]} ${className}`}
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

/**
 * The Aera mark: the company logo (the red folded-paper "A"), fitted inside the className's box.
 * Bundled through Vite, so it is served same-origin from /assets/ (the PDF renderer loads it too).
 */
export function AeraMark({ className = 'h-7 w-7' }: { className?: string }) {
  return <img src={aeraLogo} alt="Aera" aria-label="Aera" draggable={false} className={`shrink-0 object-contain ${className}`} />;
}

export function LangToggle({ lang, onChange, className = '' }: { lang: Lang; onChange: (lang: Lang) => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('common.langToggle')} className={`inline-flex rounded-full border border-hair p-0.5 text-[13px] ${className}`}>
      {(['zh', 'en'] as const).map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          onClick={() => onChange(l)}
          className={`rounded-full px-3 py-1 transition-colors ${lang === l ? 'bg-black text-white' : 'text-ink-2 hover:text-black'}`}
        >
          {t(`common.${l}`)}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-[14px] text-ink-2" role="status">
      <Loader2 className="h-4 w-4 animate-spin text-indigo" aria-hidden />
      {label}
    </div>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Makes everything beside `el`, at each level up to <body>, inert; returns the undo. */
function inertAround(el: Element): () => void {
  const made: Element[] = [];
  for (let node = el; node.parentElement && node !== document.body; node = node.parentElement) {
    for (const other of node.parentElement.children) {
      if (other === node || other.hasAttribute('inert')) continue;
      other.setAttribute('inert', '');
      made.push(other);
    }
  }
  return () => made.forEach((other) => other.removeAttribute('inert'));
}

/**
 * A bottom sheet on phones, a centred panel on wider screens. Escape and the backdrop close it. It is modal:
 * Tab and Shift+Tab go round its own controls, and the page behind it is inert until it closes.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  // Callers pass inline handlers; keyed on `open` alone, focus moves only when the sheet opens or closes,
  // not on every render while someone types in it.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement as HTMLElement | null;
    const restore = backdrop.current ? inertAround(backdrop.current) : () => undefined;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return close.current();
      const box = panel.current;
      if (e.key !== 'Tab' || !box) return;
      const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const at = document.activeElement;
      const inside = !!at && at !== box && box.contains(at);
      // From the panel itself, the last control or outside it, Tab wraps; within, it moves as usual.
      const wrap = e.shiftKey ? !inside || at === items[0] : !inside || at === items[items.length - 1];
      if (!wrap) return;
      e.preventDefault();
      (e.shiftKey ? items[items.length - 1] : items[0])?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restore();
      prev?.focus?.();
    };
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={backdrop}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE }}
            className={`max-h-[88vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-[0_30px_80px_-30px_rgba(20,16,60,.35)] outline-none sm:rounded-3xl ${wide ? 'sm:max-w-3xl' : 'sm:max-w-xl'}`}
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <h2 className="text-[18px] font-semibold">{title}</h2>
              <button type="button" onClick={onClose} className="rounded-full p-1.5 text-ink-2 hover:bg-surface" aria-label={t('common.close')}>
                <X className="h-5 w-5" />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-2xl bg-red-50 px-4 py-3 text-[14px] text-red-800">
      {children}
    </p>
  );
}

/** Copies text and briefly says so. Falls back to a hidden textarea inside the WeChat webview. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}
